import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface InviteCode {
  id: string;
  code: string;
  is_used: boolean;
  used_at: string | null;
  created_at: string;
}

export function useMyInviteCodes(options?: { enabled?: boolean }) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['my-invite-codes', user?.id],
    queryFn: async (): Promise<InviteCode[]> => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('invite_codes')
        .select('id, code, is_used, used_at, created_at')
        .eq('created_by_user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user && (options?.enabled ?? true),
  });
}

export function useGenerateInviteCode() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated');
      const code = Math.random().toString(36).substring(2, 10).toUpperCase();
      const { data, error } = await supabase
        .from('invite_codes')
        .insert({ code, created_by_user_id: user.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-invite-codes'] });
    },
  });
}
